import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { useAppTour } from '@/contexts/AppTourContext'
import { completeGuidedTourReward } from '@/hooks/useUser'
import { tourSteps } from '@/components/tour/tourSteps'
import './GuidedWalkthrough.css'

export default function GuidedWalkthrough() {
  const { isActive, currentStepIndex, nextStep, previousStep, skipTour, endTour } = useAppTour()
  const location = useLocation()
  const reward = completeGuidedTourReward()
  const layerRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const nextRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const rewardRequested = useRef(false)
  const visible = isActive && location.pathname === '/'
  const step = tourSteps[currentStepIndex]
  const isLastStep = currentStepIndex === tourSteps.length - 1

  useEffect(() => {
    if (isActive) rewardRequested.current = false
  }, [isActive])

  useEffect(() => {
    if (!visible) return
    const size = window.matchMedia?.('(max-width: 600px)').matches ? 'mobile' : 'desktop'
    tourSteps.forEach(({ id }) => {
      const image = new Image()
      image.src = `/onboarding/${id}-${size}.jpg`
    })
  }, [visible])

  useEffect(() => {
    if (!visible) return
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    const background = Array.from(document.body.children)
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== layerRef.current)
      .map(element => ({ element, inert: element.inert, inertAttribute: element.hasAttribute('inert'), ariaHidden: element.getAttribute('aria-hidden') }))
    background.forEach(({ element }) => {
      element.inert = true
      element.setAttribute('inert', '')
      element.setAttribute('aria-hidden', 'true')
    })
    document.body.style.overflow = 'hidden'
    nextRef.current?.focus({ preventScroll: true })

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        skipTour()
      } else if (event.key === 'Tab') {
        const buttons = Array.from(dialogRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') || [])
        const first = buttons[0]
        const last = buttons[buttons.length - 1]
        if (event.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
          event.preventDefault()
          last?.focus()
        } else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current?.contains(document.activeElement))) {
          event.preventDefault()
          first?.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = previousOverflow
      background.forEach(({ element, inert, inertAttribute, ariaHidden }) => {
        element.inert = inert
        element.toggleAttribute('inert', inertAttribute)
        if (ariaHidden === null) element.removeAttribute('aria-hidden')
        else element.setAttribute('aria-hidden', ariaHidden)
      })
      if (previouslyFocused?.isConnected && previouslyFocused !== document.body) {
        previouslyFocused.focus({ preventScroll: true })
      } else {
        const dashboard = document.querySelector<HTMLElement>('#root main, #root h1')
        if (dashboard) {
          const tabindex = dashboard.getAttribute('tabindex')
          dashboard.setAttribute('tabindex', '-1')
          dashboard.focus({ preventScroll: true })
          // Safari drops focus if tabindex is removed immediately after focus().
          dashboard.addEventListener('blur', () => {
            if (tabindex === null) dashboard.removeAttribute('tabindex')
            else dashboard.setAttribute('tabindex', tabindex)
          }, { once: true })
        }
      }
    }
  }, [visible, skipTour])

  // Keep keyboard focus on the primary action as the slide changes.
  useEffect(() => {
    if (visible) {
      if (contentRef.current) contentRef.current.scrollTop = 0
      nextRef.current?.focus({ preventScroll: true })
    }
  }, [visible, currentStepIndex])

  if (!visible || !step) return null

  const handleNext = () => {
    if (!isLastStep) {
      nextStep()
      return
    }
    if (!rewardRequested.current) {
      rewardRequested.current = true
      reward.mutate(undefined)
    }
    // The dashboard opens immediately, even if the reward request is slow.
    endTour()
  }

  return createPortal(
    <div ref={layerRef} className="gg-intro-layer">
      <section ref={dialogRef} className="gg-intro-dialog" role="dialog" aria-modal="true" aria-label="A quick look around GranGuard" aria-describedby="gg-intro-description">
        <header className="gg-intro-header">
          <div><span className="gg-intro-section">{step.section}</span><span className="gg-intro-count">{currentStepIndex + 1} of {tourSteps.length}</span></div>
          <button type="button" className="gg-intro-skip" onClick={skipTour}>Skip intro</button>
        </header>
        <div ref={contentRef} className="gg-intro-content">
          <div className="gg-intro-shot">
            <picture>
              <source media="(max-width: 600px)" srcSet={`/onboarding/${step.id}-mobile.jpg`} />
              <img src={`/onboarding/${step.id}-desktop.jpg`} alt={step.imageAlt} width="1100" height="640" />
            </picture>
            <svg className="gg-intro-desktop-circle" viewBox="0 0 1100 610" aria-hidden="true"><path d={step.desktopCircle} /></svg>
            <svg className="gg-intro-mobile-circle" viewBox="0 0 390 530" aria-hidden="true"><path d={step.mobileCircle} /></svg>
          </div>
          <div className="gg-intro-copy" aria-live="polite" aria-atomic="true">
            <h2>{step.title}</h2>
            <p id="gg-intro-description">{step.body}</p>
          </div>
        </div>
        <footer className="gg-intro-footer">
          <button type="button" className="gg-intro-back" disabled={currentStepIndex === 0} onClick={previousStep}>Back</button>
          <button ref={nextRef} type="button" className="gg-intro-next" onClick={handleNext}>
            {isLastStep ? 'Open dashboard' : 'Next'}<ArrowRight size={16} aria-hidden="true" />
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  )
}
